import React, { createContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import { getAllCandidates, getCandidateDetails } from '../redux/nodes/features/candidates/actions';
import {
  getCandidates,
  getCandidatesIsLoading,
  getCandidatesPagination,
  getIsSelectedCandidateDetailsLoading,
  getMoreCandidatesAreLoading,
  getSelectedCandidateDetails,
} from '../redux/nodes/features/candidates/selectors';
import { getCurrentEmployer } from '../redux/nodes/features/employers/selectors';

// eslint-disable-next-line import/no-cycle
import { useSelector } from '../utils/hooks';
import { formatCandidates } from '../utils/jobs';

import { CANDIDATES_SORT_OPTIONS_VALUES } from '../utils/constants/jobs';

export const JobCandidatesDataContext = createContext();

const withJobCandidatesData = WrappedComponent => (props) => {
  const [defaultSortField, defaultSortOrder] = CANDIDATES_SORT_OPTIONS_VALUES.APPLIED_AT_ASC.split('&');

  const dispatch = useDispatch();
  const { query } = useRouter();
  const jobId = query?.id;
  const currentEmployerId = useSelector(getCurrentEmployer);

  const [bulkActionPerformed, setBulkActionPerformed] = useState(false);
  const [hasResume, setResume] = useState(false);
  const [isBulkActionsMode, setIsBulkActionsMode] = useState(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isSortAsc, setIsSortAsc] = useState(defaultSortOrder === 'true');
  const [rating, setRating] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateProfileKey, setSelectedCandidateProfileKey] = useState(null);
  const [sortField, setSortField] = useState(defaultSortField);
  const [visibility, setVisibility] = useState({
    visible: false,
    hidden: false,
  });
  const [screenerQuestionsFilter, setScreenerQuestionsFilter] = useState(null);
  const [isRatingOverlayVisible, setIsRatingOverlayVisible] = useState(false);
  const [isEditRatingMode, setIsEditRatingMode] = useState(false);
  const [isHideCandidateConfirmationModalVisible, setIsHideCandidateConfirmationModalVisible] = useState(false);
  const [isBottomTrayVisible, setIsBottomTrayVisible] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [filtersCount, setFiltersCount] = useState(0);

  const candidates = useSelector(getCandidates);
  const isLoading = useSelector(getCandidatesIsLoading);
  const moreCandidatesLoading = useSelector(getMoreCandidatesAreLoading);
  const pagination = useSelector(getCandidatesPagination);
  const selectedCandidateDetails = useSelector(getSelectedCandidateDetails);
  const isCandidateDetailsLoading = useSelector(getIsSelectedCandidateDetailsLoading);

  const candidateIds = candidates.map(candidate => candidate.id);

  const getFilterOptions = () => {
    const filterOptions = {};

    const { visible, hidden } = visibility;

    if (searchQuery) filterOptions.search_query = encodeURIComponent(searchQuery);
    if (hasResume) filterOptions.has_resume = true;
    if (rating) filterOptions.min_rating = rating;
    if (visible && !hidden) filterOptions.is_visible = true;
    else if (!visible && hidden) filterOptions.is_visible = false;
    if (screenerQuestionsFilter?.length > 0) {
      filterOptions.screener_question_parameters = screenerQuestionsFilter;
    }

    return filterOptions;
  };

  const getSortOptions = () => ({
    sort_field: sortField,
    is_sort_asc: isSortAsc,
  });

  const loadAllCandidates = async (isLoadMore = false, isSelectAll = false) => {
    try {
      const requestBody = {
        job_id: jobId,
        organization_id: currentEmployerId,
        ...getFilterOptions(),
        ...getSortOptions(),
      };
      if (isLoadMore) {
        requestBody.page_current = pagination?.page_current + 1;
      }
      if (isSelectAll) {
        requestBody.page_current = 1;
        requestBody.page_size = pagination.element_total || 1;
      }

      const response = await dispatch(getAllCandidates({ requestBody, isLoadMore }));

      const candidates = formatCandidates(response.content);
      const candidateIds = response.content.map(item => item.id);

      return {
        candidates,
        candidateIds,
      };
    } catch (error) {
      throw error;
    }
  };

  const loadCandidateDetails = (candidateId) => {
    dispatch(getCandidateDetails({ candidateId }));
  };

  const jobCandidatesData = {
    bulkActionPerformed,
    setBulkActionPerformed,
    candidateIds,
    candidates,
    selectedCandidateDetails,
    hasResume,
    isBulkActionsMode,
    isLoading,
    isSelectAllChecked,
    isSortAsc,
    isCandidateDetailsLoading,
    loadAllCandidates,
    loadCandidateDetails,
    moreCandidatesLoading,
    pagination,
    rating,
    searchQuery,
    selectedCandidates,
    selectedCandidateIds,
    selectedCandidateIndex,
    selectedCandidateId,
    selectedCandidateProfileKey,
    setSelectedCandidateProfileKey,
    setIsBulkActionsMode,
    setIsSelectAllChecked,
    setIsSortAsc,
    setRating,
    setResume,
    setSearchQuery,
    setSelectedCandidates,
    setSelectedCandidateIds,
    setSelectedCandidateIndex,
    setSelectedCandidateId,
    setSortField,
    setVisibility,
    sortField,
    visibility,
    screenerQuestionsFilter,
    setScreenerQuestionsFilter,
    isRatingOverlayVisible,
    setIsRatingOverlayVisible,
    isEditRatingMode,
    setIsEditRatingMode,
    isHideCandidateConfirmationModalVisible,
    setIsHideCandidateConfirmationModalVisible,
    isBottomTrayVisible,
    setIsBottomTrayVisible,
    setHasUnreadMessages,
    hasUnreadMessages,
    isMessageModalOpen,
    setIsMessageModalOpen,
    candidateName,
    setCandidateName,
    filtersCount,
    setFiltersCount,
  };

  return (
    <JobCandidatesDataContext.Provider value={jobCandidatesData}>
      <WrappedComponent {...props} />
    </JobCandidatesDataContext.Provider>
  );
};
export default withJobCandidatesData;
